import { Box, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { DevicesReport } from '../../../models/DevicesReportModels';

import useRolePermissions from '../../../hooks/useRolePermissions/useRolePermissions';
import usePortalConfig from '../../../hooks/usePortalConfig/usePortalConfig';

import { setDeviceToAssign, updateRequest } from '../../../stores/AssignDeviceStore';

import MenuItemWrapper from '../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../components/Menu/MenuStyle';
import ClickableLink from '../../../components/Text/ClickableLink';

import AssignUserDrawer from './AssignUserDrawer/AssignUserDrawer';

interface DevicesReportRowOptionsProps {
  device: DevicesReport;
  isPerformingAction: boolean;
  onAssignSuccess: () => void;
  onToggleDrawer: () => void;
}
function DevicesReportRowOptions({
  device,
  isPerformingAction,
  onAssignSuccess,
  onToggleDrawer,
}: DevicesReportRowOptionsProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const dispatch = useDispatch();
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();
  const [showAssignUser, setShowAssignUser] = useState(false);

  /**
   * Watch drawer trigger variables to toggle z-index of the cell
   */
  useEffect(() => {
    onToggleDrawer();
  }, [showAssignUser]);

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Inits assign user journey
   */
  const initAssignUserJourney = () => {
    handleClose();
    dispatch(setDeviceToAssign(device));
    dispatch(updateRequest({ deviceId: device.id }));
    setShowAssignUser(true);
  };

  /**
   * Render assign option
   */
  const renderAssignOption = () => {
    if (isUserAllowed(routePermissions.assignDevices)) {
      return (
        <MenuItemWrapper onClick={initAssignUserJourney}>
          {t('pages.devicesReport.table.options.assignDevice')}
        </MenuItemWrapper>
      );
    }

    return null;
  };

  /**
   * Render edit option
   */
  const renderEditOption = () => {
    if (isUserAllowed(routePermissions.editDevices)) {
      return (
        <MenuItemWrapper
          onClick={() => {
            handleClose();
          }}
        >
          <ClickableLink
            sx={{ textDecoration: 'none', width: '100%' }}
            to={`/dashboard/unassignedReport/deviceDetails/${device.id}`}
          >
            {t('pages.devicesReport.table.options.editDevice')}
          </ClickableLink>
        </MenuItemWrapper>
      );
    }

    return null;
  };

  return (
    <>
      {isUserAllowed(routePermissions.editDevices) || isUserAllowed(routePermissions.assignDevices) ? (
        <Box
          id={`box-${device.id}`}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            borderRadius: '0.375rem',
            ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
            height: '100%',
            minWidth: '3.25rem',
          }}
          onClick={handleClick}
        >
          <VerticalDotsOutlineIcon stroke="#E60000" />
        </Box>
      ) : null}
      <Box>
        <MenuWrapper
          id={`menu-${device.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {renderEditOption()}
          {renderAssignOption()}
        </MenuWrapper>
      </Box>
      {showAssignUser ? (
        <AssignUserDrawer
          onClose={() => {
            setShowAssignUser(false);
          }}
          open={showAssignUser}
          onAssignSuccess={onAssignSuccess}
        />
      ) : null}
    </>
  );
}

export default DevicesReportRowOptions;
